import { useCallback } from 'react';
import styled from 'styled-components';

import { DestructiveButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES } from 'ui/modal';
import { fontWeights } from 'ui/theme';
import { useToastNotification } from 'utils/hooks/useToastNotification';

import { useUserDisplayName, useUserKey } from '../../hooks';
import {
  ModalState,
  UnsubscribeFromChannelsModalState,
  useModalsContext,
  useQueryContext,
} from '../contexts';
import {
  useChannelOrChannels,
  useFilteredModalState,
  useUnsubscribeUserFromChannelMutation,
} from '../hooks';

const displayText = {
  title: 'Unsubscribe from channel',
  bulkTitle: 'Unsubscribe from channels',
  confirm: 'Unsubscribe',
  loading: 'Unsubscribing...',
};

const Bold = styled.b`
  font-weight: ${fontWeights.bold};
`;

const isUnsubscribingFromChannels = (
  modalState?: ModalState | null,
): modalState is UnsubscribeFromChannelsModalState =>
  ((modalState as UnsubscribeFromChannelsModalState | undefined)?.unsubscribingFromChannels
    ?.length ?? 0) > 0;

const getChannels = (state: UnsubscribeFromChannelsModalState) => state.unsubscribingFromChannels;

export const UnsubscribeFromChannelsModal = () => {
  const userKey = useUserKey(true);
  const { hideModal } = useModalsContext();
  const unsubscribeChannels = useFilteredModalState(isUnsubscribingFromChannels, getChannels);
  const { marketingChannels } = useQueryContext();
  const userDisplayName = useUserDisplayName();

  const {
    mutate: unsubscribe,
    isSuccess,
    isLoading,
    isError,
    reset,
  } = useUnsubscribeUserFromChannelMutation();

  const channelOrChannels = useChannelOrChannels(unsubscribeChannels, marketingChannels);

  const onDone = useCallback(() => {
    hideModal();
    reset();
  }, [hideModal, reset]);

  useToastNotification({
    enabled: unsubscribeChannels.length > 0 && (isSuccess || isError),
    onDone,
    positive: isSuccess,
    negative: isError,
    positiveMessage: (
      <>
        Unsubscribed <Bold>{userDisplayName}</Bold> from <Bold>{channelOrChannels}</Bold>
      </>
    ),
    negativeMessage: (
      <>
        Could not unsubscribe <Bold>{userDisplayName}</Bold> from <Bold>{channelOrChannels}</Bold>
      </>
    ),
  });

  if (unsubscribeChannels.length === 0) {
    return null;
  }

  const handleSubmit = () => {
    unsubscribe({
      userKey,
      channelIds: unsubscribeChannels.map((c) => c.id),
    });
  };

  return (
    <Modal
      id="UnsubscribeFromChannelsModal"
      isModalOpen
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>
        {unsubscribeChannels.length === 1 ? displayText.title : displayText.bulkTitle}
      </ModalHeader>
      <ModalBody>
        Are you sure that you want to unsubscribe <Bold>{userDisplayName}</Bold> from{' '}
        <Bold>{channelOrChannels}</Bold>?
      </ModalBody>
      <ModalFooter>
        <DestructiveButton
          onClick={handleSubmit}
          data-test="confirm-unsubscribe-button"
          isDisabled={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {isLoading ? displayText.loading : displayText.confirm}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
